import type { NextPage } from "next";
import Link from "next/link";
import { useState, useEffect } from "react";
import { AnimationOnScroll } from "react-animation-on-scroll";
import Button from "../../app/component/commons/button/Button";
import Image from "../../app/component/commons/image/Image";
import Meta from "../../app/component/commons/meta/Meta";
import { AnimeContainerFacility } from "../../app/component/plugins/Anime";
import { storageFacility, advgListFacility } from "../../app/helpers/MenuList";
import DefaultLayout from "../../app/layouts/DefaultLayout";

const Index: NextPage = () => {
  const [mode, setMode] = useState("");

  useEffect(() => {
    if (window.innerWidth < 768) {
      setMode("mobile");
    } else {
      setMode("desktop");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Meta
        title="Find Qualified Buyers For Your Storage Unit Auctions | Storage Unit Auction List "
        description="Sign up for a free account on Storage Unit Auction List to list and manage your storage unit auctions. This site has over 300,000 buyers looking for auctions at storage facilities just like yours across the United States."
      />
      <DefaultLayout footerFacility>
        {/* BANNER */}
        <div className="container mx-auto">
          <div
            id="anime"
            className="min-h-[18rem] md:min-h-[43.75rem] bg-new-header bg-center bg-cover bg-no-repeat relative px-4.5 md:px-16 flex flex-col items-center justify-center"
          >
            <div
              className="absolute left-0 w-full h-full md:block hidden z-0"
              style={{
                background:
                  "linear-gradient(to right, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0), rgba(255, 255, 255, 1))",
              }}
            />
            <div
              className="absolute left-0 w-full h-full z-0 hidden"
              style={{
                background:
                  "linear-gradient(to bottom, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0), rgba(255, 255, 255, 1))",
              }}
            />
            <div className="relative z-10 flex flex-col items-center">
              <div className="max-w-[21.188rem] md:max-w-[40.375rem] text-title3 leading-[2.125rem] md:text-[2.5rem] md:leading-[3.125rem] font-bold text-neutral-900 mt-12 md:mt-0 text-center capitalize">
                Promote your{" "}
                <span className="text-gradient-7">storage auctions</span> to
                over 300,000 buyers across the country
              </div>
              <div className="mt-4.5 md:mt-7 max-w-[21.188rem] md:max-w-[40.375rem] text-neutral-700 text-small-normal leading-6 md:!text-lg text-center ">
                Storage Unit Auction List is used by storage facilities across
                the United States to manage and promote their monthly auctions.
              </div>
              <div className="mt-4.5 md:mt-12 w-[10.438rem] md:w-[10.438rem]">
                <Link href={`${process.env.NEXT_PUBLIC_BUYER_URL}/f/sign-up`}>
                  <a target="_blank">
                    <Button
                      size="lg"
                      className="!rounded-full !px-3 w-full !font-medium"
                      onClick={() => {}}
                    >
                      Register Now
                    </Button>
                  </a>
                </Link>
              </div>
            </div>
            <div id="anime-s2" className="static hidden w-full h-full md:block">
              <AnimeContainerFacility />
            </div>
          </div>
        </div>

        {/* HOW TO WORK */}
        <div className="container mx-auto">
          <div className="mt-12 md:mt-0">
            <div className="md:p-0 p-2.5 flex flex-col items-center">
              <div className="text-center font-bold text-neutral-900 md:text-title1 md:leading-[3.625rem] text-title3 leading-9 md:w-auto w-[19.938rem]">
                What is{" "}
                <span className="text-gradient-7">
                  Storage Unit Auction List?
                </span>
              </div>
              <div className="mt-3 text-neutral-700 md:text-neutral-900 text-center md:text-title3 md:leading-[2.125rem] text-small-normal leading-6 md:w-auto w-[19.938rem] px-0 md:px-3 lg:px-0">
                Use one dashboard to manage and promote all your storage
                facilities auctions
              </div>
            </div>

            {/* STEP 1 */}
            <div className="mt-7 md:mt-[5.25rem] grid grid-cols-1 md:grid-cols-2 md:gap-x-[2.125rem] gap-y-3 md:gap-y-0">
              {mode && (
                <AnimationOnScroll
                  animateIn="animate__fadeIn"
                  animateOnce
                  className="min-h-[19rem] md:min-h-[27.125rem] relative"
                  initiallyVisible={mode === "mobile" ? true : false}
                >
                  <div className="absolute w-full h-[18.75rem] gradient-third rounded-r-[2.125rem] z-0 before:absolute before:w-[40vw] before:h-full before:left-[-40vw] before:gradient-third"></div>

                  <div className="relative pl-[0] pt-[1.375rem] pb-[0] md:pt-[0]">
                    <Image
                      src="/images/step/fac-step-1-main.png"
                      alt="Step 1"
                      className="absolute w-full h-auto "
                    />
                    <Image
                      src="/images/step/fac-step-1-second.png"
                      alt="Step 1"
                      className="absolute w-[15rem] lg:w-[20rem] xl:w-2/3 h-auto right-5 top-20 md:right-[2rem] md:top-[4rem] lg:right-[2rem] lg:top-[5rem] xl:right-[2rem] xl:top-[5.8rem]"
                    />
                  </div>
                </AnimationOnScroll>
              )}
              <AnimationOnScroll
                animateIn="animate__slideInUp"
                animateOnce
                className="flex flex-col justify-center items-start space-y-4.5 max-w-[38rem] px-4.5 md:px-0 md:pr-3 lg:px-0 mt-2"
              >
                <div className="font-bold text-neutral-900 md:text-4xl md:leading-[2.723rem] text-large-normal md:capitalize">
                  A platform that storage unit buyers use to search for storage
                  unit auctions
                </div>
                <div className="text-neutral-700 md:text-neutral-900 md:text-title3 md:leading-9 text-small-normal leading-6">
                  Hundreds of thousands of storage unit buyers have signed up to
                  use Storage Unit Auction list to find storage unit auctions
                  happening around them.
                </div>
              </AnimationOnScroll>
            </div>

            {/* STEP 2 */}
            <div className="mt-7 md:mt-[10rem] grid grid-cols-1 md:grid-cols-2 md:gap-x-[2.125rem] gap-y-3 md:gap-y-0">
              <AnimationOnScroll
                animateIn="animate__fadeIn"
                animateOnce
                className="md:hidden relative min-h-[19rem]"
              >
                <div className="absolute w-full h-[18.75rem] gradient-third rounded-l-[2.125rem] z-0 before:absolute before:md:w-[40vw] before:h-full before:right-[-40vw] before:gradient-third"></div>
                <div className="relative pr-4.5 pt-4.5 pb-[0] px-0">
                  <Image
                    src="/images/step/fac-step-2-main.png"
                    alt="Step 2"
                    className="absolute w-full h-auto "
                  />
                  <Image
                    src="/images/step/fac-step-2-second.png"
                    alt="Step 2"
                    className="absolute w-[15rem] h-auto right-[4rem] top-[5rem]"
                  />
                </div>
              </AnimationOnScroll>
              <AnimationOnScroll
                animateIn="animate__slideInUp"
                animateOnce
                className="w-full flex flex-row items-center justify-end lg:px-0 md:px-0 md:pl-3 px-4.5"
              >
                <div className="flex flex-col justify-center md:items-end space-y-3 md:space-y-4.5 max-w-[39rem]">
                  <div className="font-bold text-neutral-900 md:text-4xl md:leading-[2.723rem] text-large-normal md:capitalize md:text-right">
                    Enable your Storage Facility to easily promote upcoming
                    auctions
                  </div>
                  <div className="text-neutral-700 md:text-neutral-900 md:text-title3 md:leading-9 text-small-normal leading-6 md:text-right">
                    Storage Unit Auction List provides an easy way for you to
                    publish your upcoming storage unit auctions. Your auctions
                    will be synced with our database, and buyers interested in
                    storage units will be able to see your auctions.
                  </div>
                </div>
              </AnimationOnScroll>
              <AnimationOnScroll
                animateIn="animate__fadeIn"
                animateOnce
                className="min-h-[27.125rem] md:block hidden relative"
              >
                <div className="absolute w-full h-[18.75rem] gradient-third rounded-l-[2.125rem] z-0 before:absolute before:w-[40vw] before:h-full before:right-[-40vw] before:gradient-third"></div>
                <div className="relative pt-[0] pl-[0] pr-[0] ">
                  <Image
                    src="/images/step/fac-step-2-main.png"
                    alt="Step 2"
                    className="absolute w-full h-auto "
                  />
                  <Image
                    src="/images/step/fac-step-2-second.png"
                    alt="Step 2"
                    className="absolute w-[16rem] lg:w-[22rem] xl:w-2/3 h-auto xl:right-[5rem] xl:top-[6.5rem] lg:right-[3rem] lg:top-[4rem] right-[2rem] top-[3rem]"
                  />
                </div>
              </AnimationOnScroll>
            </div>
          </div>
        </div>

        {/*  ADVANTAGE */}
        <div className="container mx-auto">
          <AnimationOnScroll
            className="mt-12 md:mt-[7.75rem] mx-auto lg:w-[60rem] xl:w-[68.125rem] gradient-main md:rounded-[4rem] md:p-16 px-4.5 md:px-4 lg:px-4.5 md:py-6 py-4.5"
            animateIn="animate__fadeIn"
            animateOnce
          >
            <AnimationOnScroll animateIn="animate__fadeIn" animateOnce>
              <div className="text-center text-title3 leading-[2.125rem] md:text-title1 md:leading-[3.625rem] font-bold text-neutral-900 lg:px-12">
                The Leading{" "} <br className="sm:hidden"/>
                <span className="text-gradient-7">Storage Unit Auction</span>{" "}
                Search Engine
              </div>
              <div className="flex justify-center">
                <div className="text-center text-small-tight leading-6 md:text-title3 md:leading-[2.125rem] md:text-neutral-900 mt-3 font-normal md:w-full w-[21.188rem] text-neutral-700">
                  List your storage facility on the most trusted storage auction
                  website in the United States.
                </div>
              </div>
            </AnimationOnScroll>

            <div className="lg:px-10 grid grid-cols-1 md:grid-cols-2 md:gap-x-5 gap-y-5 md:gap-y-12 mt-12">
              {advgListFacility.map((e, key: number) => (
                <AnimationOnScroll
                  key={key}
                  className="flex flex-col items-center p-6 bg-white rounded-3xl shadow-lv4"
                  animateIn="animate__fadeIn"
                  animateOnce
                >
                  <AnimationOnScroll animateIn="animate__zoomIn" animateOnce>
                    <Image
                      src={e.icon}
                      alt={e.title}
                      className="w-[5.875rem] h-[5.875rem]"
                    />
                  </AnimationOnScroll>

                  <AnimationOnScroll animateIn="animate__slideInUp" animateOnce>
                    <div className="text-large-normal md:text-title3 font-bold text-neutral-900 mt-3 text-center md:capitalize">
                      {e.title}
                    </div>
                    <div className="text-large-normal md:text-title3 font-bold text-neutral-900 text-center md:capitalize">
                      {e.subtitle}
                    </div>
                    <div className="text-neutral-900 text-regular-tight mt-3 text-center md:w-auto">
                      {e.desc}
                    </div>
                  </AnimationOnScroll>
                </AnimationOnScroll>
              ))}
            </div>
          </AnimationOnScroll>
        </div>

        {/* READY HUNTING AUCTIONS */}
        <div className="container mx-auto">
          <AnimationOnScroll
            animateIn="animate__fadeIn"
            className="mt-12 md:mt-[5rem] xl:mt-[7.75rem] md:mx-0 xl:mx-[5.5rem] md:w-auto px-4.5 lg:px-[5.438rem] pt-9 md:pt-[4.25rem]"
            animateOnce
          >
            <AnimationOnScroll
              animateIn="animate__slideInUp"
              animateOnce
              className="relative rounded-t-[2.125rem] md:rounded-t-[4rem] py-6 px-3 md:py-16 md:px-8 gradient-main flex flex-col items-center"
            >
              <AnimationOnScroll
                animateIn="animate__slideInRight"
                className="absolute lg:right-[-4.5rem] lg:top-[-4rem] top-[-2.5rem] right-[-1rem]"
                animateOnce
              >
                <AnimationOnScroll animateIn="animate__fadeIn" animateOnce>
                  <Image
                    src="/images/box.svg"
                    alt="Box"
                    className="lg:w-[10.5rem] lg:h-[10.5rem] w-[60px] h-[60px]"
                  />
                </AnimationOnScroll>
              </AnimationOnScroll>
              <AnimationOnScroll
                animateIn="animate__slideInLeft"
                className="absolute lg:left-[-5rem] lg:bottom-[3rem] left-[-1.5rem] bottom-0"
                animateOnce
              >
                <AnimationOnScroll animateIn="animate__fadeIn" animateOnce>
                  <Image
                    src="/images/bid.svg"
                    alt="Box"
                    className="lg:w-[10.5rem] lg:h-[10.5rem] w-[60px] h-[60px]"
                  />
                </AnimationOnScroll>
              </AnimationOnScroll>

              <AnimationOnScroll
                animateIn="animate__slideInUp"
                className="text-title3 leading-9 md:text-[3rem] md:leading-[4.5rem] font-bold text-neutral-900 text-center"
                animateOnce
              >
                Ready To Promote Your <br className="hidden sm:inline"/>
                <span className="text-gradient-7">Storage Unit Auctions?</span>
              </AnimationOnScroll>

              <AnimationOnScroll
                className="mt-3.5 md:mt-12  text-neutral-700 md:text-title3 md:leading-[3rem] text-small-normal leading-6 text-center md:font-light"
                animateIn="animate__slideInUp"
                animateOnce
              >
                Put your storage unit auctions in front of 300,000 registered &nbsp;
                <br className="hidden lg:inline"/>
                 buyers across the country.
              </AnimationOnScroll>
              <AnimationOnScroll
                className="mt-[2.375rem] md:mt-12 "
                animateIn="animate__slideInUp"
                animateOnce
              >
                <Link href={`${process.env.NEXT_PUBLIC_BUYER_URL}/f/sign-up`}>
                  <a target="_blank">
                    <Button
                      size="lg"
                      className="!rounded-full !px-3"
                      onClick={() => {}}
                    >
                      Add Your Storage Facility
                    </Button>
                  </a>
                </Link>
              </AnimationOnScroll>
            </AnimationOnScroll>
          </AnimationOnScroll>
        </div>
      </DefaultLayout>
    </>
  );
};

export default Index;
